import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { ApiError } from "../../../../api/client";
import { useEntity } from "../../../../api/queries";
import type { Family } from "../../../../domain/families";
import { FamilyMappingCard } from "../../mapping/FamilyMappingCard";
import { enabledFamiliesOf } from "../../mapping/profileState";
import { startProfile, useEntityProfiles } from "../../mapping/useProfileVersion";
import styles from "../onboard.module.css";

export const CREATE_PROFILE = "Create profile from the ERPNext pre-seed";
export const NO_PUBLISHED =
  "Every chosen document family needs a published mapping version before anything can go live.";
const SALES_NOTE = "Covers invoices, credit notes and debit notes.";

/** Where the Studio's `Back to onboarding` action returns to. */
export function returnToOnboarding(entityId: string): string {
  return `/companies/onboard?entityId=${entityId}&step=mapping`;
}

/** Step 4: one mapping card per document family the company chose; Continue waits for a
    published version of every one of them (HUB-F13). */
export function StepMapping({ entityId, onContinue }: { entityId: string; onContinue(): void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const entity = useEntity(entityId);
  const profiles = useEntityProfiles(entityId);
  const [busy, setBusy] = useState<Family | null>(null);
  const [errors, setErrors] = useState<Partial<Record<Family, string>>>({});

  const families = enabledFamiliesOf(entity.data?.enabledDocTypes);
  const profileOf = (family: Family) => profiles.data?.find((item) => item.family === family);
  const loading = profiles.isPending || entity.isPending;
  const published =
    !loading && families.every((family) => profileOf(family)?.activeVersion != null);

  async function create(family: Family): Promise<void> {
    setBusy(family);
    setErrors((prev) => ({ ...prev, [family]: "" }));
    try {
      await startProfile(entityId, family);
    } catch (exc) {
      // Someone else made it first: the refreshed list shows it, which is what the click wanted.
      if (!(exc instanceof ApiError && exc.code === "PROFILE_EXISTS")) {
        const message = exc instanceof ApiError ? exc.message : t("onboard.mapping.createError", "The profile could not be created.");
        setErrors((prev) => ({ ...prev, [family]: message }));
      }
    } finally {
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "profiles"] });
      setBusy(null);
    }
  }

  return (
    <section className={styles.panel}>
      <h2>{t("onboard.mapping.title", "Mapping")}</h2>
      <p className={styles.caption}>
        {t(
          "onboard.mapping.caption",
          "Every field the gateway needs, filled from this company's own ERP fields, one mapping per document family. The Mapping Studio does the work; this step waits for a published version of each.",
        )}
      </p>

      {loading ? <p className={styles.empty}>{t("onboard.mapping.loading", "Loading this company's profiles…")}</p> : null}

      {!loading ? (
        <div className="grid">
          {families.map((family) => (
            <FamilyMappingCard
              key={family}
              entityId={entityId}
              family={family}
              profile={profileOf(family)}
              busy={busy === family}
              error={errors[family] ?? ""}
              returnTo={returnToOnboarding(entityId)}
              createLabel={t("onboard.mapping.createProfile", CREATE_PROFILE)}
              note={family === "sales" ? t("onboard.mapping.salesNote", SALES_NOTE) : undefined}
              onCreate={(next) => void create(next)}
            />
          ))}
        </div>
      ) : null}

      {!loading && !published ? <p className={styles.empty}>{t("onboard.mapping.noPublished", NO_PUBLISHED)}</p> : null}

      <div className={styles.foot}>
        <button type="button" className="btn btn-primary" disabled={!published} onClick={onContinue}>
          {t("onboard.mapping.continue", "Continue")}
        </button>
      </div>
    </section>
  );
}
